/**
 * Fase 2 T2.5 — RubricBuilder / RubricManagement
 *
 * Studieplan-forfatter og medlem-admin oppretter og forvalter vurderingsmatriser.
 * K13: låste rubrics kan ikke endres — klon som ny versjon.
 * K16 progressive disclosure: complexity_mode = simple / graded / advanced.
 */
import { useEffect, useState } from 'react';
import { AlertCircle, Plus, Lock, Copy, Edit3, Trash2, X, ChevronDown, ChevronRight, CheckCircle } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';
import AssessmentInfoPanel from './AssessmentInfoPanel';
import { useConfirm } from './ui';

interface Rubric {
  id: string;
  title: string;
  description: string | null;
  version_number: number;
  is_locked: boolean;
  locked_at: string | null;
  complexity_mode: 'simple' | 'graded' | 'advanced';
  applicable_component_types: string[];
  member_id: string | null;
  member_name: string | null;
  created_at: string;
  cloned_from_id: string | null;
}

interface Criterion {
  id: string;
  rubric_id: string;
  sequence: number;
  title: string;
  description: string | null;
  weight_pct: number;
  levels: Array<{ label: string; points: number; is_passing?: boolean; description?: string }>;
}

const COMPONENT_TYPE_OPTIONS = [
  { value: 'practical', label: 'Praktisk demonstrasjon' },
  { value: 'project', label: 'Prosjekt' },
  { value: 'oral', label: 'Muntlig' },
  { value: 'portfolio', label: 'Mappe' },
  { value: 'observation', label: 'Observasjon' },
  { value: 'reflection', label: 'Refleksjonsnotat' },
  { value: 'group', label: 'Gruppearbeid' },
  { value: 'written', label: 'Skriftlig prøve' }
];

const COMPLEXITY_LABELS = {
  simple: 'Enkel (Bestått / Ikke bestått)',
  graded: 'Gradert (flere nivåer med poeng)',
  advanced: 'Avansert (nivåer + beskrivelse per nivå)'
};

// Standard-nivåer per complexity_mode
const DEFAULT_LEVELS: Record<string, Criterion['levels']> = {
  simple: [
    { label: 'Bestått', points: 100, is_passing: true },
    { label: 'Ikke bestått', points: 0, is_passing: false }
  ],
  graded: [
    { label: 'Utmerket', points: 100, is_passing: true },
    { label: 'Godt', points: 80, is_passing: true },
    { label: 'Tilstrekkelig', points: 60, is_passing: true },
    { label: 'Ikke bestått', points: 0, is_passing: false }
  ],
  advanced: [
    { label: 'Utmerket', points: 100, is_passing: true, description: '' },
    { label: 'Godt', points: 80, is_passing: true, description: '' },
    { label: 'Tilstrekkelig', points: 60, is_passing: true, description: '' },
    { label: 'Ikke bestått', points: 0, is_passing: false, description: '' }
  ]
};

export default function RubricManagement() {
  const { confirm } = useConfirm();
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [criteriaMap, setCriteriaMap] = useState<Record<string, Criterion[]>>({});
  const [showNewRubric, setShowNewRubric] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<{ rubricId: string; criterion?: Criterion } | null>(null);

  async function loadRubrics() {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/assessment-rubrics');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRubrics(json.rubrics || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRubrics(); }, []);

  async function loadCriteria(rubricId: string) {
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setCriteriaMap(m => ({ ...m, [rubricId]: json.criteria || [] }));
    } catch (e) {
      console.error('load criteria failed', e);
    }
  }

  function toggleExpand(id: string) {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!criteriaMap[id]) loadCriteria(id);
  }

  async function handleLock(rubric: Rubric) {
    const ok = await confirm({
      title: `Låse «${rubric.title}»?`,
      message: 'Etter låsing kan innhold ikke endres. Bruk klon for å lage en ny versjon om du trenger å endre.',
      variant: 'warning',
      confirmLabel: 'Lås matrise',
      consequences: [
        'Kriterier og nivåer kan ikke lenger endres',
        'Matrisen fryses til gjeldende versjon',
        'Klon for å opprette ny versjon med endringer',
      ],
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubric.id}/lock`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `HTTP ${res.status}`);
        return;
      }
      loadRubrics();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  async function handleClone(rubric: Rubric) {
    const ok = await confirm({
      title: 'Klone til ny versjon?',
      message: `«${rubric.title}» v${rubric.version_number} vil bli duplisert som v${rubric.version_number + 1} der du kan gjøre endringer.`,
      variant: 'info',
      confirmLabel: `Klone til v${rubric.version_number + 1}`,
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubric.id}/clone`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `HTTP ${res.status}`);
        return;
      }
      loadRubrics();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  async function handleDelete(rubric: Rubric) {
    const ok = await confirm({
      title: 'Slette vurderingsmatrise?',
      message: `«${rubric.title}» v${rubric.version_number} slettes permanent. Dette kan ikke angres.`,
      variant: 'danger',
      confirmLabel: 'Slett matrise',
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubric.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error === 'rubric_in_use' ? `Kan ikke slette — vurderingsmatrisen er brukt på ${err.practical_uses} sensur${err.practical_uses === 1 ? '' : 'er'}` : (err.error || `HTTP ${res.status}`));
        return;
      }
      loadRubrics();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  async function handleDeleteCriterion(rubricId: string, criterionId: string) {
    const ok = await confirm({
      title: 'Slette kriteriet?',
      message: 'Kriteriet fjernes fra matrisen. Dette kan ikke angres.',
      variant: 'danger',
      confirmLabel: 'Slett kriterium',
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}/criteria/${criterionId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error === 'rubric_locked' ? 'Vurderingsmatrisen er låst. Klon istedenfor å endre.' : (err.error || `HTTP ${res.status}`));
        return;
      }
      loadCriteria(rubricId);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  if (loading) return <div className="p-6 text-gray-600">Laster vurderingsmatriser…</div>;
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <div className="flex items-center gap-2 font-medium"><AlertCircle className="h-4 w-4" /> Kunne ikke hente vurderingsmatriser</div>
        <div className="text-sm mt-1">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AssessmentInfoPanel storageKey="rubric_info_open" />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Vurderingsmatriser</h2>
          <p className="text-sm text-gray-600 mt-1">
            Definer vurderingskriterier for praktisk prøve, prosjekt, muntlig og andre vurderingsformer.
            Låste vurderingsmatriser kan klones når du trenger å gjøre endringer — dette bevarer sporbarhet for allerede utstedte attester.
          </p>
        </div>
        <button
          onClick={() => setShowNewRubric(true)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-lg text-sm"
        >
          <Plus className="h-4 w-4" /> Ny vurderingsmatrise
        </button>
      </div>

      {rubrics.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
          Ingen vurderingsmatriser registrert ennå. Klikk «Ny vurderingsmatrise» for å komme i gang.
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-3"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tittel</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modus</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versjon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rubrics.map(r => (
              <>
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <button onClick={() => toggleExpand(r.id)} className="text-gray-400 hover:text-gray-700">
                      {expandedId === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{r.title}</div>
                    {r.description && <div className="text-xs text-gray-500">{r.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {r.applicable_component_types.map(t => COMPONENT_TYPE_OPTIONS.find(o => o.value === t)?.label || t).join(', ')}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{COMPLEXITY_LABELS[r.complexity_mode] || r.complexity_mode}</td>
                  <td className="px-4 py-3 text-sm">v{r.version_number}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.is_locked ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><Lock className="h-3 w-3" /> Låst</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-kf-blue-700 bg-kf-blue-50 px-2 py-0.5 rounded-full"><Edit3 className="h-3 w-3" /> Utkast</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {!r.is_locked && (
                        <button onClick={() => handleLock(r)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Lås"><Lock className="h-4 w-4" /></button>
                      )}
                      <button onClick={() => handleClone(r)} className="p-1.5 text-kf-blue-600 hover:bg-kf-blue-50 rounded" title="Klon som ny versjon"><Copy className="h-4 w-4" /></button>
                      {!r.is_locked && (
                        <button onClick={() => handleDelete(r)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Slett"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedId === r.id && (
                  <tr key={`${r.id}-details`}>
                    <td colSpan={7} className="px-3 sm:px-4 py-3 sm:py-4 bg-gray-50">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium text-gray-900">
                            Kriterier ({criteriaMap[r.id]?.length || 0})
                          </div>
                          {!r.is_locked && (
                            <button
                              onClick={() => setEditingCriterion({ rubricId: r.id, criterion: undefined })}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded"
                            >
                              <Plus className="h-3 w-3" /> Legg til kriterium
                            </button>
                          )}
                        </div>
                        {criteriaMap[r.id]?.length === 0 && (
                          <div className="text-sm text-gray-500 italic">Ingen kriterier ennå. Legg til minst ett før du låser.</div>
                        )}
                        {criteriaMap[r.id]?.map(c => (
                          <div key={c.id} className="bg-white p-3 rounded border border-gray-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="text-sm font-medium">{c.title} <span className="text-xs text-gray-500 ml-1">({c.weight_pct}%)</span></div>
                                {c.description && <div className="text-xs text-gray-600 mt-0.5">{c.description}</div>}
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {c.levels.map((lvl, i) => (
                                    <span key={i} className={`text-xs px-2 py-0.5 rounded ${lvl.is_passing !== false ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                                      {lvl.label} · {lvl.points}p
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {!r.is_locked && (
                                <div className="flex gap-1">
                                  <button onClick={() => setEditingCriterion({ rubricId: r.id, criterion: c })} className="p-1 text-kf-blue-600 hover:bg-kf-blue-50 rounded"><Edit3 className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => handleDeleteCriterion(r.id, c.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {showNewRubric && (
        <NewRubricModal
          onClose={() => setShowNewRubric(false)}
          onCreated={() => { setShowNewRubric(false); loadRubrics(); }}
        />
      )}

      {editingCriterion && (
        <CriterionEditorModal
          rubricId={editingCriterion.rubricId}
          criterion={editingCriterion.criterion}
          onClose={() => setEditingCriterion(null)}
          onSaved={() => {
            const rid = editingCriterion.rubricId;
            setEditingCriterion(null);
            loadCriteria(rid);
          }}
        />
      )}
    </div>
  );
}

/**
 * NewRubricModal — gjenbrukbar modal for å opprette ny vurderingsmatrise.
 * Eksportert for #118: inline-opprettelse fra CompositePlanEditor uten å forlate siden.
 * onCreated kalles med den nye rubric-raden slik at caller kan auto-velge den.
 */
export function NewRubricModal({ onClose, onCreated, initialComponentTypes }: {
  onClose: () => void;
  onCreated: (createdRubricId?: string) => void;
  /** Forhåndsutvalg av komponent-typer, f.eks. ['written'] når åpnet fra et skriftlig prøvesett */
  initialComponentTypes?: string[];
}) {
  const { confirm } = useConfirm();
  const [step, setStep] = useState<1 | 2>(1);
  const [rubricId, setRubricId] = useState<string | null>(null);
  const [complexityMode, setComplexityMode] = useState<'simple' | 'graded' | 'advanced'>('graded');

  // Steg 1 — metadata
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [componentTypes, setComponentTypes] = useState<string[]>(
    initialComponentTypes && initialComponentTypes.length > 0 ? initialComponentTypes : ['practical']
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Steg 2 — kriterier
  const [criteria, setCriteria] = useState<Criterion[]>([]);

  async function goToStep2() {
    if (!title || title.trim().length < 3) { setError('Tittel må ha minst 3 tegn'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/assessment-rubrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title, description,
          complexity_mode: complexityMode,
          applicable_component_types: componentTypes
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setRubricId(json.rubric.id);
      setStep(2);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  }

  async function reloadCriteria() {
    if (!rubricId) return;
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}`);
      if (res.ok) {
        const json = await res.json();
        setCriteria(json.criteria || []);
      }
    } catch { /* ignore */ }
  }

  async function deleteCriterion(cid: string) {
    if (!rubricId) return;
    const ok = await confirm({
      title: 'Slette kriteriet?',
      message: 'Kriteriet fjernes fra matrisen. Dette kan ikke angres.',
      variant: 'danger',
      confirmLabel: 'Slett kriterium',
    });
    if (!ok) return;
    try {
      await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}/criteria/${cid}`, { method: 'DELETE' });
      reloadCriteria();
    } catch { /* ignore */ }
  }

  function finish() {
    // Returnér rubricId så caller kan auto-velge den nye matrisen (brukt av CompositePlanEditor)
    onCreated(rubricId || undefined);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Ny vurderingsmatrise</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span className={step === 1 ? 'font-medium text-kf-blue-700' : ''}>1. Beskriv matrisen</span>
              <span>→</span>
              <span className={step === 2 ? 'font-medium text-kf-blue-700' : ''}>2. Legg til kriterier</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tittel</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="F.eks. «Grunnleggende mureprøve»" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse (valgfri)</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kompleksitet</label>
              <select value={complexityMode} onChange={e => setComplexityMode(e.target.value as any)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="simple">Enkel — Bestått / Ikke bestått</option>
                <option value="graded">Gradert — flere nivåer med poeng (anbefalt)</option>
                <option value="advanced">Avansert — nivåer + beskrivelse per nivå</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Bestemmer standard-nivåer på nye kriterier.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passer for komponent-typer</label>
              <div className="flex flex-wrap gap-2">
                {COMPONENT_TYPE_OPTIONS.map(opt => (
                  <label key={opt.value} className="inline-flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={componentTypes.includes(opt.value)}
                      onChange={e => {
                        if (e.target.checked) setComponentTypes([...componentTypes, opt.value]);
                        else setComponentTypes(componentTypes.filter(t => t !== opt.value));
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
                <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
              <button onClick={goToStep2} disabled={saving} className="px-4 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-2">
                {saving ? 'Lagrer…' : 'Neste — legg til kriterier'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && rubricId && (
          <div className="space-y-4">
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3 text-sm text-kf-blue-900">
              <div className="font-medium mb-1">Matrisen «{title}» er opprettet.</div>
              <div className="text-xs">Legg til ett eller flere kriterier. Klikk «Ferdig» når du er ferdig — du kan alltid legge til flere senere.</div>
            </div>

            {criteria.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-900">Kriterier ({criteria.length})</div>
                {criteria.map(c => (
                  <div key={c.id} className="bg-gray-50 border border-gray-200 rounded p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{c.title} <span className="text-xs text-gray-500 ml-1">({c.weight_pct}%)</span></div>
                        {c.description && <div className="text-xs text-gray-600 mt-0.5">{c.description}</div>}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {c.levels.map((lvl, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded ${lvl.is_passing !== false ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                              {lvl.label} · {lvl.points}p
                            </span>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => deleteCriterion(c.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <InlineCriterionForm
              rubricId={rubricId}
              defaultLevels={DEFAULT_LEVELS[complexityMode]}
              nextSequence={criteria.length}
              onAdded={reloadCriteria}
            />

            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                {criteria.length === 0
                  ? 'Legg til minst ett kriterium før du fullfører — eller lukk for å legge til senere.'
                  : `${criteria.length} kriterier lagt til`
                }
              </div>
              <button onClick={finish} className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Ferdig
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Inline-form for å legge til et kriterium — brukes i steg 2 av NewRubricModal.
 * Etter «Legg til»-klikk tømmer formen seg og listen oppdateres.
 */
function InlineCriterionForm({
  rubricId,
  defaultLevels,
  nextSequence,
  onAdded
}: {
  rubricId: string;
  defaultLevels: Criterion['levels'];
  nextSequence: number;
  onAdded: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [weightPct, setWeightPct] = useState(100);
  const [levels, setLevels] = useState<Criterion['levels']>(defaultLevels);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  function updateLevel(i: number, patch: Partial<Criterion['levels'][0]>) {
    setLevels(lvls => lvls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  }
  function addLevel() {
    setLevels([...levels, { label: '', points: 0, is_passing: false }]);
  }
  function removeLevel(i: number) {
    setLevels(levels.filter((_, idx) => idx !== i));
  }

  function reset() {
    setTitle('');
    setDescription('');
    setWeightPct(100);
    setLevels(defaultLevels);
    setError(null);
  }

  async function add() {
    if (!title || title.trim().length < 2) { setError('Tittel må ha minst 2 tegn'); return; }
    if (levels.length < 2) { setError('Minst 2 nivåer kreves'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}/criteria`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, weight_pct: weightPct, levels, sequence: nextSequence })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      reset();
      setExpanded(false);
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-kf-blue-400 hover:text-kf-blue-700 hover:bg-kf-blue-50 transition-colors"
      >
        <Plus className="h-4 w-4" /> Legg til kriterium
      </button>
    );
  }

  return (
    <div className="bg-white border border-kf-blue-200 rounded-lg p-3 space-y-3">
      <div className="text-sm font-medium text-kf-blue-900">Nytt kriterium</div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Tittel</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="F.eks. «Bruker verktøy korrekt»" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Utfyllende forklaring (valgfri)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Vekt (%)</label>
        <input type="number" min={0} max={100} step={1} value={weightPct} onChange={e => setWeightPct(parseFloat(e.target.value || '0'))} className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
        <p className="text-xs text-gray-500 mt-1">100 % hvis eneste kriterium. Ved flere kriterier bør sum av vekter være 100 %.</p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-700">Nivåer</label>
          <button onClick={addLevel} type="button" className="text-xs text-kf-blue-600 hover:text-kf-blue-800 inline-flex items-center gap-1">
            <Plus className="h-3 w-3" /> Legg til nivå
          </button>
        </div>
        <div className="space-y-2">
          {levels.map((lvl, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 p-2 border border-gray-200 rounded">
              <input value={lvl.label} onChange={e => updateLevel(i, { label: e.target.value })} placeholder="Nivå-etikett" className="flex-1 min-w-[100px] border border-gray-300 rounded px-2 py-1 text-sm" />
              <input type="number" value={lvl.points} onChange={e => updateLevel(i, { points: parseFloat(e.target.value || '0') })} className="w-20 border border-gray-300 rounded px-2 py-1 text-sm" />
              <span className="text-xs text-gray-500">p</span>
              <label className="inline-flex items-center gap-1 text-xs">
                <input type="checkbox" checked={lvl.is_passing !== false} onChange={e => updateLevel(i, { is_passing: e.target.checked })} /> Bestått
              </label>
              <button onClick={() => removeLevel(i)} type="button" className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={() => { reset(); setExpanded(false); }} className="px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
        <button onClick={add} disabled={saving} className="px-3 py-1.5 text-xs bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> {saving ? 'Legger til…' : 'Legg til kriterium'}
        </button>
      </div>
    </div>
  );
}

function CriterionEditorModal({ rubricId, criterion, onClose, onSaved }: {
  rubricId: string;
  criterion?: Criterion;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!criterion;
  const [title, setTitle] = useState(criterion?.title || '');
  const [description, setDescription] = useState(criterion?.description || '');
  const [weightPct, setWeightPct] = useState(criterion?.weight_pct ?? 100);
  const [levels, setLevels] = useState<Criterion['levels']>(criterion?.levels || DEFAULT_LEVELS.graded);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateLevel(i: number, patch: Partial<Criterion['levels'][0]>) {
    setLevels(lvls => lvls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  }
  function addLevel() {
    setLevels([...levels, { label: '', points: 0, is_passing: false }]);
  }
  function removeLevel(i: number) {
    setLevels(levels.filter((_, idx) => idx !== i));
  }

  async function save() {
    if (!title || title.trim().length < 2) { setError('Tittel må ha minst 2 tegn'); return; }
    if (levels.length < 2) { setError('Minst 2 nivåer kreves'); return; }
    setSaving(true);
    setError(null);
    try {
      const url = isEdit
        ? `/api/admin/assessment-rubrics/${rubricId}/criteria/${criterion!.id}`
        : `/api/admin/assessment-rubrics/${rubricId}/criteria`;
      const res = await authenticatedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, weight_pct: weightPct, levels })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error === 'rubric_locked' ? 'Vurderingsmatrisen er låst — klon istedenfor' : (err.error || `HTTP ${res.status}`));
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isEdit ? 'Rediger' : 'Nytt'} kriterium</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kriterium-tittel</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="F.eks. «Bruker verktøy korrekt»" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Utfyllende forklaring (valgfri)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vekt (%)</label>
            <input type="number" min={0} max={100} step={1} value={weightPct} onChange={e => setWeightPct(parseFloat(e.target.value || '0'))} className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
            <p className="text-xs text-gray-500 mt-1">100 hvis eneste kriterium. Sum av alle kriterier i rubric bør være 100 for weighted-modus.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Nivåer (velg poeng per nivå)</label>
              <button onClick={addLevel} type="button" className="text-xs text-kf-blue-600 hover:text-kf-blue-800 inline-flex items-center gap-1">
                <Plus className="h-3 w-3" /> Legg til nivå
              </button>
            </div>
            <div className="space-y-2">
              {levels.map((lvl, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border border-gray-200 rounded">
                  <input value={lvl.label} onChange={e => updateLevel(i, { label: e.target.value })} placeholder="Nivå-etikett" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
                  <input type="number" value={lvl.points} onChange={e => updateLevel(i, { points: parseFloat(e.target.value || '0') })} className="w-24 border border-gray-300 rounded px-2 py-1 text-sm" />
                  <span className="text-xs text-gray-500">p</span>
                  <label className="inline-flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={lvl.is_passing !== false} onChange={e => updateLevel(i, { is_passing: e.target.checked })} /> Bestått
                  </label>
                  <button onClick={() => removeLevel(i)} type="button" className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> {saving ? 'Lagrer…' : 'Lagre'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
